import React, { useEffect } from "react";
import {useFormContext} from "react-hook-form";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import {TransferDirectionEnum} from "../../enums/TransferDirectionEnum";
import {TransportEnum} from "../../enums/TransferTransportEnum";
import Department from "../../types/Department";
import useTransferRouteGetArrival from "../../hooks/useTransferRouteGetArrival";
import useTransferRouteGetDeparture from "../../hooks/useTransferRouteGetDeparture";
import TransferDirectionSection from "../sectionComponents/TransferDirectionSection";
import ArrivalTransferSection from "../sectionComponents/ArrivalTransferSection";
import DepartureTransferSection from "../sectionComponents/DepartureTransferSection";
import EventCity from "../../../eventCities/types/EventCity";

type Props = {
    department?: Department;
    eventCity?: EventCity;
};


const TransferRequestTransportDetails: React.FC<Props> = ({department, eventCity}) => {
    const { control, watch, setValue, formState: { errors } } = useFormContext();
    const t = useTranslate();

    const direction = watch("direction");
    const arrivalTransport = watch("arrivalTransport");
    const departureTransport = watch("departureTransport");
    const city = watch("city");
    const arrivalLocationFrom = watch("arrivalLocationFrom");
    const arrivalLocationTo = watch("arrivalLocationTo");
    const departureLocationFrom = watch("departureLocationFrom");
    const departureLocationTo = watch("departureLocationTo");

    // Only fetch routes if it's the "individuals" department
    const shouldFetchRoutes = department?.name?.toLowerCase() === "individuals"
        || department?.name?.toLowerCase() === "individual";

    // Get transfer routes - ARRIVAL
    const { route: arrivalRoute, isLoading: arrivalRouteLoading } = useTransferRouteGetArrival({
        cityId: city, arrivalLocationFrom, arrivalLocationTo, execute: shouldFetchRoutes,
    });

    // Get transfer routes - DEPARTURE
    const { route: departureRoute, isLoading: departureRouteLoading } = useTransferRouteGetDeparture({
        cityId: city, departureLocationFrom, departureLocationTo, execute: shouldFetchRoutes,
    });

    // Save the complete route when obtained (to send all vehicles)
    useEffect(() => {
        if (arrivalRoute) { setValue("arrivalRoute", arrivalRoute) }
    }, [arrivalRoute, setValue]);

    useEffect(() => {
        if (departureRoute) { setValue("departureRoute", departureRoute) }
    }, [departureRoute, setValue]);

    // Clear reference fields when transport is OTHER
    useEffect(() => {
        if (arrivalTransport === TransportEnum.OTHER) {
            setValue("arrivalTransportReference", "");
            setValue("arrivalTransportReferenceNumber", "");
        }
    }, [arrivalTransport, setValue]);

    useEffect(() => {
        if (departureTransport === TransportEnum.OTHER) {
            setValue("departureTransportReference", "");
            setValue("departureTransportReferenceNumber", "");
        }
    }, [departureTransport, setValue]);

    // Clear vehicle when ARRIVAL transport changes
    useEffect(() => {
        setValue("arrivalVehicle", "");
    }, [arrivalTransport, setValue]);

    // Clear vehicle and route when ARRIVAL locations change
    useEffect(() => {
        setValue("arrivalVehicle", "");
        setValue("arrivalRoute", null); // Clear previous route to search for a new one
    }, [arrivalLocationFrom, arrivalLocationTo, setValue]);

    // Clear vehicle when DEPARTURE transport changes
    useEffect(() => {
        setValue("departureVehicle", "");
    }, [departureTransport, setValue]);

    // Clear vehicle and route when DEPARTURE locations change
    useEffect(() => {
        setValue("departureVehicle", "");
        setValue("departureRoute", null); // Clear previous route to search for a new one
    }, [departureLocationFrom, departureLocationTo, setValue]);

    const showArrivalSection = direction === TransferDirectionEnum.ARRIVAL || direction === TransferDirectionEnum.ROUND_TRIP;
    const showDepartureSection = direction === TransferDirectionEnum.DEPARTURE || direction === TransferDirectionEnum.ROUND_TRIP;

    return (
        <div className="space-y-6">
            {/* --- TRANSFER DETAILS HEADER --- */}
            <div className="pb-4 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    {t("transfers.fields.transferDetails")}
                </h3>
            </div>
            {/* --- TYPE OF TRANSFER --- */}
            <TransferDirectionSection department={department} />
            {/* --- ARRIVAL SECTION (INBOUND) --- */}
            {showArrivalSection && (
                <ArrivalTransferSection 
                    arrivalTransport={arrivalTransport}
                    arrivalRoute={arrivalRoute}
                    arrivalRouteLoading={arrivalRouteLoading}
                    errors={errors}
                    control={control}
                    department={department}
                    eventCity={eventCity}
                />
            )}
            {/* --- DEPARTURE SECTION (OUTBOUND) --- */}
            {showDepartureSection && (
                <DepartureTransferSection 
                    departureTransport={departureTransport}
                    departureRoute={departureRoute}
                    departureRouteLoading={departureRouteLoading}
                    errors={errors}
                    control={control}
                    department={department}
                    eventCity={eventCity}
                />
            )}
        </div>
    );
};

export default TransferRequestTransportDetails;
